'use client';

import { useEffect, useMemo } from 'react';
import { DEFAULT_EXPRESSION, type RainaAnimation, type RainaExpression, useAnimationState } from './useAnimationState';
import { useAnimationTriggers } from './useAnimationTriggers';
import { useIdleAnimations } from './useIdleAnimations';

export type { RainaExpression, RainaAnimation };
export { DEFAULT_EXPRESSION };

export interface UseRainaAnimationOptions {
  /** Current expression to display */
  expression?: RainaExpression;
  /** Animation(s) to trigger */
  animation?: RainaAnimation | RainaAnimation[];
  /** Enable idle animations like periodic blinking (default: true) */
  idleAnimations?: boolean;
  /** Callback when an animation completes */
  onAnimationComplete?: (animation: RainaAnimation) => void;
  /** Blink interval range in ms [min, max] (default: [3000, 5000]) */
  blinkInterval?: [number, number];
  /** Chance of a double-blink (0-1, default: 0.2 = 20%) */
  doubleBlinkChance?: number;
}

export interface UseRainaAnimationResult {
  /** Whether the wave animation is active */
  isWaving: boolean;
  /** Whether a blink is occurring */
  isBlinking: boolean;
  /** Whether the nod animation is active */
  isNodding: boolean;
  /** Whether an ear twitch is occurring */
  isEarTwitching: boolean;
  /** Whether a head tilt is occurring */
  isHeadTilting: boolean;
  /** Direction of head tilt ('left' or 'right') */
  headTiltDirection: 'left' | 'right';
  /** The current expression */
  currentExpression: RainaExpression;
  /** Trigger a wave animation manually */
  triggerWave: () => void;
  /** Trigger a blink manually */
  triggerBlink: () => void;
  /** Trigger a nod manually */
  triggerNod: () => void;
  /** Trigger an ear twitch manually */
  triggerEarTwitch: () => void;
  /** Trigger a head tilt manually */
  triggerHeadTilt: () => void;
  /** Handler to attach to animated elements' onAnimationEnd */
  handleAnimationEnd: (animation: RainaAnimation) => void;
}

/**
 * Hook for managing Raina's animations and expressions.
 *
 * Uses a state machine pattern with animation queuing for smooth,
 * predictable animations without conflicts.
 *
 * @example
 * ```tsx
 * const { isWaving, isBlinking, triggerWave, handleAnimationEnd } = useRainaAnimation({
 *   expression: 'happy',
 *   idleAnimations: true,
 * });
 *
 * return (
 *   <div
 *     className={isWaving ? 'animate-wave' : ''}
 *     onAnimationEnd={() => handleAnimationEnd('wave')}
 *   />
 * );
 * ```
 */
export function useRainaAnimation(options: UseRainaAnimationOptions = {}): UseRainaAnimationResult {
  const {
    expression = 'happy',
    animation,
    idleAnimations = true,
    onAnimationComplete,
    blinkInterval = [3000, 5000],
    doubleBlinkChance = 0.2,
  } = options;

  const { state, dispatch, canStartAnimation } = useAnimationState();

  const { triggerWave, triggerBlink, triggerNod, triggerEarTwitch, triggerHeadTilt, handleAnimationEnd } = useAnimationTriggers({
    dispatch,
    canStartAnimation,
    onAnimationComplete,
  });

  useIdleAnimations({
    idleAnimations,
    blinkInterval,
    doubleBlinkChance,
    triggerBlink,
    triggerEarTwitch,
    triggerHeadTilt,
  });

  useEffect(() => {
    if (!animation) return;

    const animations = Array.isArray(animation) ? animation : [animation];
    for (const anim of animations) {
      if (anim !== 'idle') {
        if (anim === 'headTilt') {
          triggerHeadTilt();
        } else if (anim === 'wave') {
          triggerWave();
        } else if (anim === 'blink') {
          triggerBlink();
        } else if (anim === 'nod') {
          triggerNod();
        } else if (anim === 'earTwitch') {
          triggerEarTwitch();
        }
      }
    }
  }, [animation, triggerWave, triggerBlink, triggerNod, triggerEarTwitch, triggerHeadTilt]);

  return useMemo(
    () => ({
      isWaving: state.waving,
      isBlinking: state.blinking,
      isNodding: state.nodding,
      isEarTwitching: state.earTwitching,
      isHeadTilting: state.headTilting,
      headTiltDirection: state.headTiltDirection,
      currentExpression: expression,
      triggerWave,
      triggerBlink,
      triggerNod,
      triggerEarTwitch,
      triggerHeadTilt,
      handleAnimationEnd,
    }),
    [
      state.waving,
      state.blinking,
      state.nodding,
      state.earTwitching,
      state.headTilting,
      state.headTiltDirection,
      expression,
      triggerWave,
      triggerBlink,
      triggerNod,
      triggerEarTwitch,
      triggerHeadTilt,
      handleAnimationEnd,
    ],
  );
}
